/**
 * A dónde depositarle al proveedor, según el método que el revendedor eligió.
 *
 * 🚨 POR QUÉ EXISTE. El modal de pago pedía método, monto, código de operación y captura — todo
 *    menos el dato sin el cual nada de eso se puede hacer: EL NÚMERO AL QUE TRANSFERIR. Elegías
 *    «Yape» y no había número de Yape.
 *
 *    Es la clase de hueco que no rompe ningún test: el formulario valida bien, guarda bien, y es
 *    inusable. Lo encontró el founder abriendo la pantalla.
 *
 * 🪤 SE MUESTRA SÓLO LA CUENTA DEL MÉTODO ELEGIDO. Listar las cinco es cómo alguien yapea a un
 *    número de cuenta bancaria. Si elegís Yape, ves el Yape.
 *
 * Los datos salen de `tenant_business_profile.bank_accounts` del proveedor — el lugar canónico de
 * los datos bancarios de un negocio. No hay una copia en el módulo: dos verdades sobre a dónde te
 * pagan terminan mandando plata al número equivocado.
 */
import { useState } from 'react';
import { Box, Text, InlineStack, BlockStack } from '@/lib/polaris';
import { AppButton } from '@/components/admin/AppButton';
import { AppBanner } from '@/components/admin/AppBanner';

/** Una fila de `bank_accounts`. Todo opcional: los perfiles viejos vienen incompletos. */
export interface CuentaDeCobro {
  bank?: string;
  number?: string;
  cci?: string;
  holder?: string;
  account_type?: string;
}

/**
 * Qué cuenta corresponde a cada método.
 *
 * 🪤 `transfer` toma la primera cuenta que NO sea una billetera: una transferencia bancaria no se
 * hace a un número de Yape. `cash` no tiene cuenta — se paga en persona.
 */
export function cuentaParaMetodo(cuentas: CuentaDeCobro[], metodo: string): CuentaDeCobro | null {
  const porBanco = (nombre: string) =>
    cuentas.find((c) => (c.bank ?? '').toLowerCase().includes(nombre)) ?? null;

  if (metodo === 'yape') return porBanco('yape');
  if (metodo === 'plin') return porBanco('plin');
  if (metodo === 'transfer') {
    return cuentas.find((c) => {
      const b = (c.bank ?? '').toLowerCase();
      return b !== '' && !b.includes('yape') && !b.includes('plin');
    }) ?? null;
  }
  return null; // efectivo
}

/**
 * Los métodos que este proveedor ACEPTA DE VERDAD, derivados de sus cuentas.
 *
 * 🪤 ANTES ERA UNA LISTA FIJA con Yape, Plin, transferencia y efectivo. Vitalex no tiene Plin ni
 *    cobra en efectivo, así que dos de las cuatro opciones llevaban a un callejón sin salida:
 *    elegís Plin, no hay número de Plin, y el revendedor se queda mirando la pantalla.
 *
 *    Ofrecer un medio de pago que el proveedor no acepta es peor que no ofrecerlo. Y como sale de
 *    los datos, el día que alguien cargue su Plin aparece solo — sin deploy, igual que el segundo
 *    proveedor.
 *
 * 🪤 EFECTIVO NO ESTÁ, y es una decisión: este catálogo despacha a provincia por agencia. No hay
 *    un mostrador donde pagar en efectivo. Si algún día lo hay, se agrega acá.
 */
export function metodosDisponibles(cuentas: CuentaDeCobro[]): { label: string; value: string }[] {
  const opciones: { label: string; value: string }[] = [];
  const conNumero = (cuentas ?? []).filter((c) => (c.number ?? '').trim() !== '');

  if (conNumero.some((c) => (c.bank ?? '').toLowerCase().includes('yape'))) {
    opciones.push({ label: 'Yape', value: 'yape' });
  }
  if (conNumero.some((c) => (c.bank ?? '').toLowerCase().includes('plin'))) {
    opciones.push({ label: 'Plin', value: 'plin' });
  }
  // La transferencia se rotula con el banco: «Transferencia BCP» dice más que «Transferencia».
  const banco = conNumero.find((c) => {
    const b = (c.bank ?? '').toLowerCase();
    return b !== '' && !b.includes('yape') && !b.includes('plin');
  });
  if (banco) {
    opciones.push({ label: `Transferencia${banco.bank ? ` ${banco.bank}` : ''}`, value: 'transfer' });
  }
  return opciones;
}

/**
 * Una línea copiable.
 *
 * 🪤 SIN ETIQUETA ARRIBA. La versión anterior ponía «Número» sobre el número y «Titular» sobre el
 *    titular — dos líneas para decir algo que el contexto ya dice, en un modal que no entraba en
 *    pantalla. Un dato con etiqueta obvia gasta el doble de alto y no informa el doble.
 *
 *    El CCI sí lleva prefijo, porque un número largo al lado de otro número largo sin decir cuál es
 *    cuál es exactamente cómo se transfiere al lugar equivocado.
 */
function LineaCopiable({ prefijo, valor }: { prefijo?: string; valor: string }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
      <Text as="span" variant="bodyMd" fontWeight="semibold" breakWord>
        {prefijo ? <Text as="span" variant="bodySm" tone="subdued">{prefijo} </Text> : null}
        {valor}
      </Text>
      {/*
        🪤 COPIAR NO ES UN LUJO ACÁ: son números de 13 y 20 dígitos que se tipean mirando la
        pantalla del celular. Un dígito mal y la plata se va a otra cuenta.
      */}
      <AppButton
        variant="plain"
        onClick={() => {
          void navigator.clipboard?.writeText(valor).then(
            () => { setCopiado(true); setTimeout(() => setCopiado(false), 1500); },
            () => { /* sin portapapeles: el número igual está a la vista */ },
          );
        }}
      >
        {copiado ? 'Copiado' : 'Copiar'}
      </AppButton>
    </InlineStack>
  );
}

export function ADondeDepositar(
  { cuentas, metodo, proveedor }: { cuentas: CuentaDeCobro[]; metodo: string; proveedor: string },
) {
  const cuenta = cuentaParaMetodo(cuentas, metodo);

  // 🪤 Sin datos NO se muestra un recuadro vacío: se dice qué falta y a quién pedírselo. Un
  //    espacio en blanco deja al revendedor adivinando si es un error suyo.
  if (!cuenta || !cuenta.number) {
    return (
      <AppBanner tone="warning" title={`Todavía no hay una cuenta cargada para ${metodo === 'transfer' ? 'transferencia' : metodo}`}>
        <Text as="p" variant="bodySm">
          Escribile a {proveedor} para que te pase el número.
        </Text>
      </AppBanner>
    );
  }

  return (
    <Box padding="300" background="bg-surface-secondary" borderRadius="200">
      <BlockStack gap="150">
        <Text as="span" variant="bodyXs" tone="subdued">
          Depositá a {proveedor}
          {cuenta.bank ? ` · ${cuenta.bank}` : ''}
          {cuenta.account_type ? ` · ${cuenta.account_type}` : ''}
        </Text>
        <LineaCopiable valor={cuenta.number} />
        {/* El CCI sólo aplica a transferencias interbancarias; en Yape no significa nada. */}
        {metodo === 'transfer' && cuenta.cci ? <LineaCopiable prefijo="CCI" valor={cuenta.cci} /> : null}
        {cuenta.holder ? (
          <Text as="span" variant="bodyXs" tone="subdued" breakWord>{cuenta.holder}</Text>
        ) : null}
      </BlockStack>
    </Box>
  );
}
